import { Link } from 'react-router-dom';
import { ArrowRight, Mic, MessageSquare, Users, Building2, Lightbulb, Heart } from 'lucide-react';

const DR_GRIFFIN_PHOTO = "https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/xfj6pmgv_Gemini_Generated_Image_twtrvgtwtrvgtwtr.png";

export default function Speaking() {
  const speakingTopics = [
    {
      icon: Heart,
      title: 'Turning Pain Into Purpose',
      description: 'How to transform personal tragedy into meaningful work and renewed direction.'
    },
    {
      icon: Users,
      title: 'Leading Through Loss',
      description: 'Strategies for high-functioning leaders navigating grief while maintaining performance.'
    },
    {
      icon: Building2,
      title: 'Building Grief-Competent Organizations',
      description: 'Creating cultures that support employees through loss without productivity collapse.'
    },
    {
      icon: MessageSquare,
      title: 'The Language of Loss',
      description: 'What to say, what not to say, and how to create space for healing conversations.'
    },
    {
      icon: Lightbulb,
      title: 'Movement & Meaning After Loss',
      description: 'Practical frameworks for finding forward motion when grief feels paralyzing.'
    },
    {
      icon: Mic,
      title: 'Custom Topics',
      description: 'Tailored presentations for your specific audience, industry, or organizational needs.'
    }
  ];

  const eventTypes = [
    'Corporate conferences & retreats',
    'Healthcare & hospital staff events',
    'Church & ministry gatherings',
    'Nonprofit leadership summits',
    'Community awareness events',
    'Professional development workshops',
    'Academic conferences',
    'Memorial & remembrance services'
  ];

  return (
    <div className="overflow-hidden" data-testid="speaking-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#FAFAF8] relative" data-testid="speaking-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                Speaking & Events
              </span>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1B365D] mb-6">
                Book Dr. Griffin to Speak
              </h1>
              
              <p className="text-lg text-[#1B365D]/70 leading-relaxed mb-8">
                Dr. Angelia Griffin delivers powerful, transformative keynotes that help audiences understand grief, build resilience, and find renewed purpose. Her talks combine academic insight with deeply personal lived experience.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-[#C9A962]/20 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-[#1B365D]/60">Keynote Starting At</span>
                    <p className="font-serif text-3xl font-bold text-[#1B365D]">$7,500</p>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-[#2D7D7B] flex items-center justify-center">
                    <Mic className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
              
              <a href="https://griefassessment.agcthefixer.info/contact-724806" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="speaking-cta">
                Inquire About Booking
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A962]/20 to-transparent rounded-3xl" />
              <img 
                src={DR_GRIFFIN_PHOTO}
                alt="Dr. Angelia Griffin"
                className="relative rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-24 md:py-32 bg-white" data-testid="speaking-topics">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              Keynote Topics
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-4">
              Speaking Topics
            </h2>
            <p className="text-lg text-[#1B365D]/70">
              Each presentation can be customized to your audience's specific needs and context.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {speakingTopics.map((topic, index) => (
              <div 
                key={topic.title}
                className="service-card group"
                data-testid={`speaking-topic-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#2D7D7B] to-[#1F5A58] flex items-center justify-center mb-6">
                  <topic.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1B365D] mb-3 group-hover:text-[#2D7D7B] transition-colors">
                  {topic.title}
                </h3>
                <p className="text-[#1B365D]/70 leading-relaxed">
                  {topic.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-24 bg-[#1B365D] relative" data-testid="speaking-events">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                Event Types
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
                Perfect For Your Event
              </h2>
              <p className="text-lg text-white/70 leading-relaxed">
                Dr. Griffin speaks at a variety of events, from intimate workshops to large conferences. Her message adapts to your context while maintaining its transformative power.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {eventTypes.map((type, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="w-2 h-2 rounded-full bg-[#C9A962]" />
                  <span className="text-white">{type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Talk Show */}
      <section className="py-24 md:py-32 bg-[#F5F5F0]" data-testid="speaking-talkshow">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#C9A962]/20 text-center">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                Weekly Talk Show
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-6">
                All That Matters
              </h2>
              <p className="text-lg text-[#1B365D]/70 leading-relaxed mb-8 max-w-2xl mx-auto">
                Join Dr. Griffin weekly for conversations about grief, resilience, leadership, and finding meaning after loss. Real stories, practical wisdom, and compassionate guidance.
              </p>
              <div className="section-divider mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Book */}
      <section className="py-24 bg-white" data-testid="speaking-book">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                Author
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-6">
                "Turning In Is The Only Way Out"
              </h2>
              <p className="text-lg text-[#1B365D]/70 leading-relaxed mb-6">
                In her transformative book, Dr. Griffin shares her personal journey through grief and the insights that have helped countless others find their path forward. A must-read for anyone navigating loss or supporting those who are.
              </p>
              <p className="font-serif italic text-[#2D7D7B] text-lg">
                Available for bulk orders for event attendees.
              </p>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="flex items-center justify-center">
                <a 
                  href="https://www.amazon.com/Turning-Only-Way-Angelia-Griffin/dp/1737840510"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-90 transition-opacity"
                  data-testid="book-amazon-link"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/81z6o0gr_61J7wT-Jg3L._SY522_.jpg"
                    alt="Turning In Is The Only Way Out by Dr. Angelia Griffin"
                    className="w-64 md:w-80 rounded-lg shadow-2xl cursor-pointer hover:shadow-3xl transition-shadow"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2D7D7B] relative" data-testid="speaking-final-cta">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
              Bring Dr. Griffin to Your Event
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Transform your next gathering with a powerful message of resilience, hope, and purpose.
            </p>
            <a href="https://griefassessment.agcthefixer.info/contact-724806" target="_blank" rel="noopener noreferrer" className="bg-white text-[#2D7D7B] hover:bg-[#F5F5F0] px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center justify-center gap-2" data-testid="speaking-book-cta">
              Request Speaking Information
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
